<template>
	<div
		class="help-launcher"
		data-testid="help-assistant-launcher"
	>
		<button
			v-show="!helpAssistantOpen"
			ref="fab"
			type="button"
			data-testid="help-assistant-fab"
			class="help-launcher__fab"
			aria-label="Open help assistant"
			@click="openHelpAssistant"
		>
			<lucide-icon
				name="WandSparkles"
				:size="24"
			/>
		</button>

		<sliding-sheet
			ref="sheet"
			:is-open="helpAssistantOpen"
			title="Help Assistant"
			width="760px"
			close-button-aria-label="Close help assistant"
			data-testid="help-assistant-sheet"
			role="dialog"
			aria-modal="true"
			aria-label="Help Assistant"
			@close="closeHelpAssistant"
		>
			<help-assistant-panel
				v-if="helpAssistantOpen"
				context="admin-portal"
			/>
		</sliding-sheet>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import { SlidingSheet } from '../../component-library-wrapper.js'
import HelpAssistantPanel from './help-assistant-panel.vue'
import { helpAssistantOpen, openHelpAssistant, closeHelpAssistant } from '../../../../../shared/state/help-assistant.js'

// Matches the focusable-elements query used by the React reference's local focus-trap hook
// (syrenis-frontends/apps/consent-configuration/src/hooks/useFocusTrap.ts) - the library
// `sliding-sheet.vue` provides neither Esc-to-close nor a focus trap nor focus-return-to-trigger
// (unlike the React launcher's Radix `Sheet`, which owns all three), so this component supplies
// the WCAG 2.2 keyboard-operability behavior itself.
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Floating help-assistant launcher (AB#26561 Task 9): a fixed-position FAB that opens the
 * shared component-library `sliding-sheet` hosting `<help-assistant-panel>`. Mounted once, at
 * app shell level (`app.vue`), gated on `enableHelpAssistant`; the splash page's "Help Assistant"
 * overview card drives the same `helpAssistantOpen` singleton so either entry point opens the one
 * sheet instance.
 */
export default {
	name: 'help-launcher',
	components: {
		LucideIcon,
		SlidingSheet,
		HelpAssistantPanel
	},
	setup () {
		return {
			helpAssistantOpen,
			openHelpAssistant,
			closeHelpAssistant
		}
	},
	watch: {
		helpAssistantOpen (isOpen) {
			if (isOpen) {
				document.addEventListener('keydown', this.handleKeydown)
				this.$nextTick(this.focusSheet)
			} else {
				document.removeEventListener('keydown', this.handleKeydown)
				this.$nextTick(() => this.$refs.fab?.focus())
			}
		}
	},
	mounted () {
		// `helpAssistantOpen` is a module-level singleton that outlives this component (this
		// launcher itself unmounts/remounts as `helpLauncherVisible` in app.vue flips - e.g.
		// across a logout/login or a route change). The `watch` above only fires on a
		// true/false transition, so a remount while the sheet is already open would otherwise
		// come up with no Esc/focus-trap listener attached and no focus moved into the sheet.
		// Only sync the "opened" side here - not the "closed" else-branch - so a normal mount
		// with the sheet closed never steals focus onto the FAB.
		if (this.helpAssistantOpen) {
			document.addEventListener('keydown', this.handleKeydown)
			this.$nextTick(this.focusSheet)
		}
	},
	beforeDestroy () {
		document.removeEventListener('keydown', this.handleKeydown)
	},
	methods: {
		handleKeydown (event) {
			if (event.key === 'Escape') {
				this.closeHelpAssistant()
				return
			}
			if (event.key === 'Tab') this.trapFocus(event)
		},
		getFocusableElements () {
			const root = this.$refs.sheet?.$el
			if (!root || typeof root.querySelectorAll !== 'function') return []
			return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(el => el.offsetParent !== null)
		},
		focusSheet () {
			this.getFocusableElements()[0]?.focus()
		},
		trapFocus (event) {
			const focusable = this.getFocusableElements()
			if (focusable.length === 0) return
			const first = focusable[0]
			const last = focusable[focusable.length - 1]
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault()
				last.focus()
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault()
				first.focus()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.help-launcher {
  &__fab {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    color: var(--primary-foreground, #FFFFFF);
    cursor: pointer;
    background-color: var(--primary, #5C7B9D);
    border: none;
    border-radius: var(--radius-full, 9999px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
      transform: translateY(-2px);
    }

    &:focus-visible {
      outline: 2px solid var(--primary, #5C7B9D);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      &:hover {
        transform: none;
      }
    }
  }
}
</style>
